import { FC, useMemo, useCallback, useState } from 'react';
import { Loading, Section, Input, Button, Table } from '../../../../components';
import { moneyFormatter } from '../../../../utils';
import { useToastStore } from '../../../../stores';
import {
	getAllPayment,
	TAnuncioPagamento
} from '../../../../services';
import {
	Form,
	FullLine,
	HalfLine,
	HalfLineButton,
	NoData,
	Separator,
	TableContainer,
	TableWrapper,
	TitleContainer,
} from '../../Reports.styles';

interface IGraphLineData {
	label: string;
	qtde: number;
}

const AdminShopkeeperDelayPayments: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [filteredOffers, setFilteredOffers] = useState<TAnuncioPagamento[]>([]);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [clickGenerate, setClickGenerate] = useState(false);

	const fetchData = async () => {
		setLoading(true);
		let body = {};
		if (startDate && endDate && endDate >= startDate) {
			body = {
				dataCadastroInicial: `${startDate}T00:00:00.000Z`,
				dataCadastroFinal: `${endDate}T23:59:59.999Z`,
			};
		}
		const data = await getAllPayment(body);
		if (!data) {
			setToast({ variant: 'error', message: 'Erro ao buscar dados!' });
			setLoading(false);
			return;
		}

		const filtered = data.filter((payment) => {

			const matchesDelay = payment.dataPagamento && (new Date(payment.dataVencimento!) < new Date(payment.dataPagamento!))

			return matchesDelay;
		});

		const salesCountByMonthYear: IGraphLineData[] = [];
		filtered.forEach((offer) => {
			const dataPagamento = new Date(offer.dataPagamento!);
			const label = `${dataPagamento.getMonth() + 1}/${dataPagamento.getFullYear()}`;
			const existingEntry = salesCountByMonthYear.find((entry) => entry.label === label);

			if (existingEntry) {
				existingEntry.qtde++;
			} else {
				salesCountByMonthYear.push({ label, qtde: 1 });
			}
		});

		setFilteredOffers(filtered);
		setClickGenerate(true);
		setLoading(false);
	};

	const tableColumns = useMemo(
		() => [

			{ id: 'registerDate', label: 'Dt Cobrança', width: '100px' },
			{ id: 'plate', label: 'Placa' },
			{ id: 'dealer', label: 'Anunciante' },
			{ id: 'buyer', label: 'Comprador' },
			{ id: 'dueDate', label: 'Dt Vencimento', width: '100px' },
			{ id: 'paymentDate', label: 'Dt Pagamento', width: '100px' },
			{ id: 'value', label: 'Valor Cobrado' },
			{ id: 'valuePaid', label: 'Valor Pago' },
		],
		[]
	);

	const rowMapper = useCallback(
		(payment: TAnuncioPagamento) => ({
			plate: {
				value: payment.anuncio?.placa,
				rawValue: payment.anuncio?.placa,
			},
			dealer: {
				value: payment.anuncio?.concessionaria.fantasia,
				rawValue: payment.anuncio?.concessionaria.fantasia,
			},
			buyer: {
				value: payment.revenda.fantasia,
				rawValue: payment.revenda.fantasia,
			},
			registerDate: {
				value: new Date(payment.dataCadastro!).toLocaleDateString('pt-BR'),
				rawValue: new Date(payment.dataCadastro!).getTime(),
			},
			dueDate: {
				value: new Date(payment.dataVencimento || '').toLocaleDateString('pt-BR'),
				rawValue: new Date(payment.dataVencimento || '').getTime(),
			},
			paymentDate: {
				value: new Date(payment.dataPagamento || '').toLocaleDateString('pt-BR'),
				rawValue: new Date(payment.dataPagamento || '').getTime(),
			},
			value: {
				value: moneyFormatter(payment.valorCobrado),
				rawValue: payment.valorCobrado,
			},
			valuePaid: {
				value: moneyFormatter(payment.valorPagamento || 0),
				rawValue: payment.valorPagamento || 0,
			},
		}),
		[]
	);

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={filteredOffers?.map(rowMapper)} paginate exportToPDF exportToCSV />,
		[filteredOffers, rowMapper, tableColumns]
	);

	return (
		<Section>
			<TitleContainer>Relatório de Compradores que atrasam pagamentos</TitleContainer>
			<Separator />
			<Form>
				<HalfLine>
					<Input
						label="Data de Cobrança Inicial"
						type="date"
						variant='text'
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Cobrança Final"
						type="date"
						variant='text'
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						min={startDate}
					/>
				</HalfLine>
				<HalfLineButton>
					<FullLine>
						<HalfLineButton>
							<Button
								variant='large'
								fitContent
								onClick={fetchData}
							>
								Gerar
							</Button>
						</HalfLineButton>
					</FullLine>
				</HalfLineButton>
			</Form>
			{loading && <Loading />}
			<TableContainer>
				{(loading || (filteredOffers?.length || 0) > 0) && (
					<TableWrapper>{loading ?  <Loading /> : MemoizedTable}</TableWrapper>
				)}
				{!loading && clickGenerate && filteredOffers?.length === 0 && (
					<NoData>Nenhum registro encontrado</NoData>
				)}
			</TableContainer>
			
		</Section>
	);
};

export default AdminShopkeeperDelayPayments;
